import * as RadixPopover from '@radix-ui/react-popover';
import { cx } from 'class-variance-authority';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'cmdk';
import { AnimatePresence, easeOut, motion } from 'framer-motion';
import { forwardRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../../../loading-spinner';
import type { Option, SelectAll } from '../../multi-select';
import CheckboxCheck from './checkbox-check';

type ContentProps = {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  hideSelectAll?: boolean;
  options: Option[];
  onSearchChange?: (search: string) => void;
  emptyState?: ReactNode;
  isLoading?: boolean;
  value: string[];
  handleInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  selectAll?: SelectAll;
};

const Content = forwardRef<HTMLDivElement, ContentProps>(
  (
    {
      onOpenChange,
      open,
      options,
      value,
      handleInputKeyDown,
      selectAll,
      hideSelectAll,
      onSearchChange,
      emptyState,
      isLoading,
    },
    ref,
  ) => {
    const { t } = useTranslation('ui', {
      keyPrefix: 'components.multi-select',
    });
    const isAllSelected = value.length === options.length;

    return (
      <AnimatePresence>
        {open && (
          <RadixPopover.Content
            className={cx(
              'p-0 w-[var(--radix-popover-trigger-width)] relative z-tooltip',
              'bg-elevated rounded border border-primary',
              'shadow-md',
              'overflow-y-auto origin-[var(--radix-popover-content-transform-origin)]',
            )}
            align="start"
            onEscapeKeyDown={() => onOpenChange(false)}
            side="bottom"
            sideOffset={4}
            avoidCollisions={true}
            collisionPadding={8}
            collisionBoundary={document.body}
            asChild
            forceMount
          >
            <motion.div
              ref={ref}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{
                duration: 0.1,
                ease: easeOut,
              }}
            >
              <Command shouldFilter={!onSearchChange}>
                <div className="relative">
                  <CommandInput
                    // @ts-ignore: fails for dashboard project due to i18next selector
                    placeholder={t('search-placeholder-default')}
                    onKeyDown={handleInputKeyDown}
                    onValueChange={onSearchChange}
                    className="w-full pl-3 pr-8 border-b text-tertiary text-body-2 min-h-9 focus:outline-none border-tertiary"
                  />
                  {isLoading && (
                    <LoadingSpinner size="compact" className="absolute right-3 top-3 pointer-events-none" />
                  )}
                </div>
                <CommandList>
                  <CommandEmpty className="p-3 text-quaternary text-body-2">
                    {/* @ts-ignore: fails for dashboard project due to i18next selector */}
                    {emptyState ?? t('empty-state-text-default')}
                  </CommandEmpty>
                  <CommandGroup className="overflow-y-auto max-h-[300px] py-1">
                    {selectAll && !hideSelectAll && (
                      <CommandItem
                        onSelect={selectAll.onSelect}
                        className="px-1 pb-1 mb-1 overflow-hidden border-b border-dashed rounded cursor-pointer border-tertiary"
                      >
                        <div className="flex items-center w-full max-w-full gap-2 px-2 py-1 rounded cursor-pointer select-none hover:bg-hover">
                          <span className="relative flex items-center justify-center w-4 h-4 min-w-4 min-h-4">
                            <input
                              type="checkbox"
                              checked={isAllSelected}
                              // @ts-ignore: fails for dashboard project due to i18next selector
                              aria-label={selectAll.label || t('select-all-label-default')}
                              onClick={event => event.stopPropagation()}
                              onChange={selectAll.onSelect}
                              className={`
                          grid w-4 h-full min-w-4 min-h-4
                          place-content-center
                          border rounded
                          bg-primary border-primary
                          transition-colors duration-100
                          appearance-none
                          outline-offset-1
                          focus:outline-none
                          checked:bg-tertiary checked:border-transparent
                          disabled:bg-senary disabled:border-transparent
                          focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--text-color-accent)]
                      `}
                            />
                            <CheckboxCheck isSelected={isAllSelected} />
                          </span>
                          <span className="w-full font-medium text-body-2 text-primary">
                            {/* @ts-ignore: fails for dashboard project due to i18next selector */}
                            {selectAll.label || t('select-all-label-default')}
                          </span>
                        </div>
                      </CommandItem>
                    )}
                    {options.map(option => {
                      const isSelected = value.includes(option.value);
                      return (
                        <CommandItem
                          key={option.value}
                          disabled={option.disabled}
                          onSelect={() => option.toggleOption?.(option.value)}
                          className="px-1 overflow-hidden rounded cursor-pointer data-[disabled=true]:cursor-default"
                        >
                          <div
                            className={cx(
                              'flex items-center w-full max-w-full gap-2 px-2 py-1 rounded select-none',
                              option.disabled ? 'cursor-default' : 'cursor-pointer hover:bg-hover',
                            )}
                          >
                            <span className="relative flex items-center justify-center w-4 h-4 min-w-4 min-h-4">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={option.disabled}
                                aria-label={option.label}
                                onClick={event => event.stopPropagation()}
                                onChange={() => option.toggleOption?.(option.value)}
                                className={`
                            grid w-4 h-full min-w-4 min-h-4
                            place-content-center
                            border rounded
                            bg-primary border-primary
                            transition-colors duration-100
                            appearance-none
                            outline-offset-1
                            focus:outline-none
                            checked:bg-tertiary checked:border-transparent
                            disabled:bg-senary disabled:border-transparent
                            focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--text-color-accent)]
                        `}
                              />
                              <CheckboxCheck isSelected={isSelected} />
                            </span>
                            <span
                              className={cx('w-full text-body-2', option.disabled ? 'text-tertiary' : 'text-primary')}
                            >
                              {option.label}
                            </span>
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </motion.div>
          </RadixPopover.Content>
        )}
      </AnimatePresence>
    );
  },
);

export default Content;
